import { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import {
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
} from '@mui/material';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import BarChart from 'components/ui/charts/BarChart';
import colorGenerator from 'utils/colorGenerator';

/**
 * Lidlar oqimi — MANBA (CrmField) kesimida: qaysi kuni qaysi manbadan qancha lid.
 * Manba maydonini CrmField'dan tanlanadi (masalan "Ofisga keldi").
 */
export default function LeadsBySource() {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();

  const [fieldId, setFieldId] = useState('');
  const [start, setStart] = useState(
    moment().startOf('month').format('YYYY-MM-DD'),
  );
  const [finish, setFinish] = useState(moment().format('YYYY-MM-DD'));

  // Manba maydonlari (CrmField)
  const { data: fields } = useQuery({
    queryKey: ['/analytic/crm/crm-fields'],
    queryFn: async () =>
      (await axiosPrivate.get('/analytic/crm/crm-fields')).data.data,
    onSuccess: (d) => {
      if (!fieldId && d?.length) setFieldId(d[0].id);
    },
    retry: false,
  });

  // Manba kesimidagi lidlar
  const { data, isFetching } = useQuery({
    queryKey: ['/analytic/crm/leads-source', fieldId, start, finish],
    enabled: !!fieldId,
    queryFn: async () =>
      (
        await axiosPrivate.get(
          `/analytic/crm/leads-source?field_id=${fieldId}&start=${start}&finish=${finish}`,
        )
      ).data.data,
    retry: false,
  });

  const { labels, datasets, sources, sourceTotals, total } = useMemo(() => {
    const rows = data?.data || [];
    const srcs = data?.sources || [];
    return {
      labels: rows.map((r) => moment(r.date).format('DD.MM')),
      datasets: srcs.map((s) => ({
        label: s || '—',
        data: rows.map((r) => r.counts?.[s] || 0),
        backgroundColor: colorGenerator(),
        borderRadius: 4,
        stack: 'leads',
      })),
      sources: srcs,
      sourceTotals: data?.sourceTotals || {},
      total: data?.total || 0,
    };
  }, [data]);

  return (
    <div className='space-y-4'>
      {/* Manba maydoni + sana */}
      <div className='flex flex-wrap items-center gap-3 py-2'>
        <FormControl size='small' className='min-w-[220px]'>
          <Select
            value={fieldId}
            onChange={(e) => setFieldId(e.target.value)}
            displayEmpty
          >
            <MenuItem value='' disabled>
              {t('boss.crm.lead.sourceField') || 'Manba maydoni'}
            </MenuItem>
            {(fields || []).map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <input
          type='date'
          value={start}
          onChange={(e) => setStart(e.target.value)}
          className='border border-gray-300 rounded-lg px-2 py-1.5 text-sm'
        />
        <input
          type='date'
          value={finish}
          onChange={(e) => setFinish(e.target.value)}
          className='border border-gray-300 rounded-lg px-2 py-1.5 text-sm'
        />
        <span className='text-sm text-gray-500'>
          {t('common.table.total')}: <b>{total}</b>
        </span>
      </div>

      {isFetching ? (
        <div className='flex justify-center py-12'>
          <CircularProgress size={30} />
        </div>
      ) : datasets.length === 0 ? (
        <div className='sale-chart !base-border !shadow-md'>
          <span className='no-data-found-wrapper'>
            <i className='bi bi-exclamation-octagon text-lg mr-1' />{' '}
            {t('common.global.noDataFound')}
          </span>
        </div>
      ) : (
        <>
          {/* Kunlik, manba bo'yicha stacked bar */}
          <div className='sale-chart !base-border !shadow-md'>
            <div className='h-[360px]'>
              <BarChart
                chartLabels={labels}
                chartDatasets={datasets}
                options={{
                  scales: {
                    x: { stacked: true },
                    y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
                  },
                }}
                optionPlugins={{
                  legend: {
                    display: true,
                    position: 'bottom',
                    labels: { usePointStyle: true, pointStyle: 'rectRounded' },
                  },
                }}
              />
            </div>
          </div>

          {/* Manba bo'yicha jami */}
          <div className='sale-chart !base-border !shadow-md'>
            <table className='w-full text-sm'>
              <thead>
                <tr className='text-gray-500 text-xs uppercase'>
                  <th className='text-left py-2'>{t('boss.crm.lead.sourceField') || 'Manba'}</th>
                  <th className='text-right py-2'>{t('boss.crm.lead.leads')}</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((s) => (
                  <tr key={s} className='border-t border-gray-100'>
                    <td className='py-1.5'>{s || '—'}</td>
                    <td className='py-1.5 text-right font-medium'>
                      {sourceTotals[s] || 0}
                    </td>
                  </tr>
                ))}
                <tr className='border-t-2 border-gray-200 font-semibold'>
                  <td className='py-1.5'>{t('common.table.total')}</td>
                  <td className='py-1.5 text-right'>{total}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
